(ns frontend.view.modals.guest-invite.view
  (:require
   [clojure.string :as string]
   [common.email :as email]
   [common.localization :as loc]
   [common.util :as cu]
   [frontend.logging :as logging]
   [frontend.state.current-session :as session]
   [frontend.updates.entity-scope :as entity-scope]
   [frontend.utils :as fu]
   [frontend.view.components.select :as select-comp]
   [frontend.view.entity :as f-entity]
   [frontend.view.modals.components.processing-modal :as processing-modal]
   [frontend.view.modals.guest-invite.events :as events]
   [reagent.core :as r]
   [ui.components.modal.view :as modal]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.editors.open-search.view :as open-search]))

(defonce modal-key :guest-invite-modal)

(defn has-email?
  [entity]
  (let [entity-type (:entity/type entity)]
    (case entity-type
      :entity.type/person (email/valid-email-pattern? (:person/universal-email-address entity))
      :entity.type/account (email/valid-email-pattern? (:account/email entity))
      (do (logging/error "Unexpected entity type as email recipient : " entity)
          false))))

(defn render-option
  [{:keys [selected? isFocused entity-type props label entity value]}]
  (let [avatar-url (:person/avatar-url entity)
        avatar-url (fu/only-https-or-relative-img avatar-url)
        picture-url (:account/picture-url entity)
        picture-url (fu/only-https-or-relative-img picture-url)]
    [:div
     (merge
      {:class-name (fu/cs :select-option__contact
                          (when selected?
                            :is-selected)
                          (when isFocused
                            :is-focused)
                          (when-not has-email?
                            :email-missing)
                          (when (= :entity.type/account entity-type)
                            :account))}
      (:innerProps (js->clj props)))
     [:span
      {:class-name (fu/cs :select-option__contact__circle
                          (when-not has-email?
                            :email-missing))}
      (cond
        (= :entity.type/person entity-type) (cond
                                              avatar-url
                                              [:img {:src (fu/sanitize-img-url avatar-url)}]

                                              (or (:person/first-name entity)
                                                  (:person/last-name entity))
                                              [:span
                                               (str (first (:person/first-name entity))
                                                    ""
                                                    (first (:person/last-name entity)))]

                                              :else
                                              [:i.fal.fa-at])
        (= :entity.type/account entity-type) (cond picture-url
                                                   [:img
                                                    {:src (fu/sanitize-img-url picture-url)}]

                                                   :else
                                                   [:span
                                                    (str (first (:account/first-name entity))
                                                         ""
                                                         (first (:account/last-name entity)))]))]
     [:span.select-option__contact__label.ml-2
      (or (:person/universal-email-address entity)
          (:account/email entity)
          label
          (->> value
               :label)
          (->> value
               :new-value))
      ]]))

(defn render-recipient-option
  [select-val props]
  (let [{:keys [:isDisabled
                :value
                :isSelected
                :isFocused
                :label
                :isRtl]}
        props
        value (-> props
                  (js->clj :keywordize true)
                  :value)
        entity-id (when (and value
                             (coll? value))                                                         ;; value is [id display-string] pair
                    (first value))]
    (if entity-id
      (let [entity (get @session/entities entity-id)
            getStyles (:getStyles props)
            selected? (or isSelected
                          (and (coll? select-val)
                               (some #{entity-id} select-val))
                          (= select-val entity-id))
            entity-type (:entity/type entity)
            has-email? (has-email? entity)]
        (render-option {:selected?   selected?
                        :isFocused   isFocused
                        :entity-type entity-type
                        :props       props
                        :label       label
                        :entity      entity
                        :value       value}))
      (render-option {:selected? isSelected
                      :isFocused isFocused
                      :props     props
                      :label     label
                      :value     value}))))

(defn render-empty-component
  [props]
  )

(defn guest-selector
  [state props]
  (let [field-atom (r/atom {})]
    (fn [state props]
      (let [selected-person-id (:selected-person-id @state)]
        [:div.field-editor__content
         [:div.field-editor__content__header
          [:div.field-editor__content__header__label
           [:label
            {:class-name (fu/cs "field-editor__content__header__label__text-content")}
            (loc/tr :email)]
           [:i.fas.fa-asterisk.mandatory]]
          [:span.empty-space]]

         [:div.field-editor__content__field-input
          [:div.field-editor__content__field-input__primary-content
           [open-search/single-value
            {:value              nil
             :placeholder        (loc/tr :type-to-search)
             :field/entity-types [:entity.type/person]
             :create!            (fn [input-str]
                                   (f-entity/open-creator!
                                    [:entity.type/person]
                                    {:start-value       input-str
                                     :skip-success-msg? true
                                     :on-ok!            (fn [{:keys [new-entity-id close-cb!]}]
                                                          (if (number? new-entity-id)
                                                            (events/select-person! state new-entity-id))
                                                          (when (fn? close-cb!)
                                                            (close-cb!)))}))
             :on-change          (fn [value option]
                                   (when (number? value)
                                     ;; The prefill reads the person from the session.
                                     ;; A new-grid account does not have it there.
                                     (entity-scope/hydrate!
                                      {:entity-id value
                                       :on-done   (fn [] (events/select-person! state value))})))}]
           #_[select-comp/select-creatable
              {:is-multi            false
               :menu-portal-target  (.getElementById js/document "react-select-menu-container")
               :value               selected-person-id
               :placeholder         (when-not selected-person-id
                                      (:placeholder props))
               :on-change!          (fn [sv]
                                      (events/select-person! state sv))
               :escape-clears-value true
               :ref                 (fn [component]
                                      (when (and component
                                                 (not= component (:select-component @field-atom)))
                                        (swap! field-atom assoc :select-component component)))
               :loading-placeholder " "                                                             ;loading-text
               :auto-blur           false
               :components          {:Option             (r/reactify-component (partial render-recipient-option
                                                                                        selected-person-id))
                                     :DropdownIndicator  (r/reactify-component render-empty-component)
                                     :IndicatorSeparator (r/reactify-component render-empty-component)
                                     :ClearIndicator     (r/reactify-component render-empty-component)}
               :get-option-value    (fn [args]
                                      args)
               :get-option-label    (fn [eid-or-new-option]
                                      (let [cla (js->clj eid-or-new-option :keywordize-keys true)
                                            res (cond
                                                  (:new-value cla) (:label cla)
                                                  (string? cla) cla
                                                  (number? cla) (let [entity (get @session/entities
                                                                                  eid-or-new-option)]
                                                                  (or (:person/universal-email-address
                                                                       entity)
                                                                      (:account/email entity)))
                                                  (and (coll? cla)
                                                       (= (count cla) 2)
                                                       (second cla))
                                                  (second cla)
                                                  :else
                                                  (logging/error
                                                   "Could not resolve email select-creatable value "
                                                   cla))]
                                        res))
               :default-options     true
               :is-valid-new-option
               (fn [args]
                 (let [lcase (cu/str-trim-lcase args)]
                   (if (empty? lcase)
                     false
                     (let [res (and (email/valid-email-pattern? lcase)
                                    (not (some #{lcase} (:new-options @field-atom)))
                                    (let [options (select-comp/get-options
                                                   {:value-type        :entity
                                                    :entity-types      [:entity.type/person]
                                                    :entities-atom     session/entities
                                                    :entity-types-atom session/entity-types
                                                    :has-focus?        true
                                                    :search-str-lc     lcase
                                                    :filter-fn         (select-comp/filter-person-option
                                                                        {:query-mandatory-attributes
                                                                         [:person/universal-email-address]
                                                                         :query-attributes
                                                                         [:person/universal-email-address
                                                                          :person/first-name
                                                                          :person/last-name]})})
                                          filtered-options
                                          (->> options
                                               (filter (fn [[eid v]]
                                                         (let [person (get @session/entities eid)
                                                               email (:person/universal-email-address
                                                                      person)]
                                                           (-> email
                                                               (str)
                                                               (string/lower-case)
                                                               (= lcase))))))]
                                      (empty? filtered-options)))]
                       res))))
               :get-new-option-data (fn [args]
                                      (let [trimmed (cu/str-trim-lcase args)]
                                        {:new-value  trimmed
                                         :icon       (fn [data]
                                                       [:i.fal.fa-plus-circle.create-option__icon])
                                         :class-name "create-option select-dropdown-menu-button"
                                         :label      (loc/tr :create-email-recipient [trimmed])}))
               :value-type          :entity
               :entity-types        [:entity.type/person]
               :on-create-option    (fn [args]
                                      (f-entity/open-creator!
                                       :entity.type/person
                                       {:start-value      args
                                        :mandatory-fields [:person/universal-email-address]
                                        :on-ok!           (fn [{:keys [new-entity-id close-cb!]}]
                                                            (events/select-person! state new-entity-id)
                                                            (close-cb!))}))
               ;        :on-create-option    (fn [args]
               ;                               (on-save! attribute (concat value [args]))
               ;                               (swap! field-atom assoc :new-options (concat (:new-options @field-atom)
               ;                                                                            [args])))
               :load-select-options
               (partial
                select-comp/load-options
                {:temp-atom       field-atom
                 :search-function (fn [input-lc results-cb]
                                    (results-cb
                                     (take 50
                                           (select-comp/get-options
                                            {:value-type        :entity
                                             :entity-types      [:entity.type/person]
                                             :entities-atom     session/entities
                                             :entity-types-atom session/entity-types
                                             :has-focus?        (:has-focus? @field-atom)
                                             :search-str-lc     input-lc
                                             :filter-fn         (select-comp/filter-person-option
                                                                 {:query-mandatory-attributes
                                                                  [:person/universal-email-address]
                                                                  :query-attributes
                                                                  [:person/universal-email-address
                                                                   :person/first-name
                                                                   :person/last-name]})}))))
                 })}]]]]))))

(defn validate-new-account-field
  [attr value]
  (case attr
    :new-email (when-not (email/valid-email-pattern? value)
                 (loc/tr :email-must-be-valid [value]))
    (when (fu/empty-value? value)
      (loc/tr :mandatory-field/value-not-set [(loc/tr (case attr
                                                        :new-first-name :account/first-name
                                                        :new-last-name  :account/last-name))]))))

(defn account-input
  [state {:keys [attr label data-type dom-id]}]
  [:div
   [labelled-input/view
    {:dom-id     dom-id
     :data-type  data-type
     :label      label
     :mandatory? true
     :value      (get @state attr)
     :on-change  (fn [new-value]
                   (swap! state assoc attr new-value))
     :on-blur    (fn [_]
                   (swap! state assoc-in [:error-message attr]
                          (validate-new-account-field attr (get @state attr))))}]
   [:p {:class "text-xs text-danger-light-text"}
    (get-in @state [:error-message attr])]])

(defn create-account
  [state]
  [:div
   {:class "flex flex-col gap-3"}
   [account-input state {:attr      :new-email
                         :label     (loc/tr :account/email)
                         :data-type :data.type/email
                         :dom-id    "new-guest-email"}]
   [account-input state {:attr      :new-first-name
                         :label     (loc/tr :account/first-name)
                         :data-type :data.type/text-simple
                         :dom-id    "new-guest-first-name"}]
   [account-input state {:attr      :new-last-name
                         :label     (loc/tr :account/last-name)
                         :data-type :data.type/text-simple
                         :dom-id    "new-guest-last-name"}]])

(defn view-body
  [state params]
  (let [{:keys []} params]
    [:div
     (if-not (:selected-person-id @state)
       [guest-selector state]
       [create-account state])]))

(defn footer-view
  [{:keys [disabled? target-id guest-role state on-close!]}]
  [:<>
   [modal/left-cancel-only on-close! nil]
   [modal/right-single-button
    nil
    disabled?
    (fn [e]
      (events/invite-guest! target-id guest-role state))
    (loc/tr :invite-guest)
    nil]])

(defn view
  [{:keys [shown? close-modal! props state class-name topmost?]}]
  (fn [{:keys [shown? close-modal! props state class-name topmost?]}]
    (let [{:keys [target-id guest-role]} props
          disabled? (or (not (email/valid-email-pattern? (:new-email @state)))
                        (fu/empty-value? (:new-first-name @state))
                        (fu/empty-value? (:new-last-name @state)))]
      [processing-modal/render-modal
       {:state             state
        :shown?            (and shown? topmost?)
        :success-text      (loc/tr :success-guest-invited)
        :on-close!         close-modal!
        :class-name        "overflow-initial"
        :default-title-fn  #(loc/tr :add-guest-user)
        :default-body-fn   (partial view-body state)
        :default-footer-fn (partial footer-view
                                    {:disabled?  disabled?
                                     :target-id  target-id
                                     :guest-role guest-role
                                     :state      state
                                     :on-close!  close-modal!})}])))
